import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { Loader2, Building2, CheckCircle, ArrowLeft, Send } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const LOGO = 'https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png';

const BLANK = {
  company_name: '', company_reg: '', duns_number: '', vat_number: '',
  address_line1: '', address_line2: '', address_city: '', address_county: '', address_postcode: '', address_country: 'United Kingdom',
  eori_number: '', ukims_number: '',
  contact_name: '', trade_contact: '', contact_email: '', secondary_emails: '', contact_phone: '', phone_2: '', fax: '',
  website: '', twitter: '', linkedin: '', facebook: '',
  payment_terms_days: 30, notes: '',
};

export default function B2BApply() {
  const { toast } = useToast();
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const f = (k) => ({ value: form[k] || '', onChange: (e) => setForm((p) => ({ ...p, [k]: e.target.value })) });
  const num = (k) => ({ value: form[k] ?? 0, onChange: (e) => setForm((p) => ({ ...p, [k]: e.target.value === '' ? '' : Number(e.target.value) })) });

  const submit = async () => {
    if (!form.company_name.trim()) { toast({ title: 'Company name is required', variant: 'destructive' }); return; }
    if (!form.contact_email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) {
      toast({ title: 'A valid contact email is required', variant: 'destructive' }); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        secondary_emails: form.secondary_emails ? form.secondary_emails.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      const res = await base44.functions.invoke('b2bSubmitApplication', payload);
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Submission failed');
      setDone(true);
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <img src={LOGO} alt="Motion Reality" className="h-12 mx-auto mb-8 object-contain" />
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-green-400/40 bg-green-400/10 mb-6">
            <CheckCircle className="w-7 h-7 text-green-400" />
          </div>
          <h1 className="text-white font-black text-2xl sm:text-3xl uppercase tracking-tight mb-3">Application received</h1>
          <p className="text-white/40 text-sm leading-relaxed mb-8">
            Thanks — our trade team will review your details and be in touch within 1–2 business days. Once approved, you'll receive an email inviting you to set up your online trade account.
          </p>
          <Link to="/b2b" className="inline-flex items-center gap-2 text-primary hover:underline font-bold text-xs tracking-widest">
            <ArrowLeft className="w-4 h-4" /> BACK TO TRADE PORTAL
          </Link>
        </div>
      </div>
    );
  }

  const Section = ({ title, children }) => (
    <div className="border border-white/10 rounded p-5 bg-card">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-3 pb-2 border-b border-primary/20">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
  const F = ({ label, children, full }) => (
    <div className={full ? 'sm:col-span-2' : ''}><label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">{label}</label>{children}</div>
  );

  return (
    <div className="min-h-screen bg-background pt-24 pb-20">
      <header className="px-6 lg:px-10 py-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <Link to="/b2b" className="text-white/30 text-xs font-bold tracking-widest hover:text-primary transition-colors mb-4 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> BACK TO TRADE PORTAL
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">B2B</p>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-3xl uppercase">Apply for a trade account</h1>
          <p className="text-white/30 text-sm mt-1 max-w-xl">
            Fill in your business details below. Our team will review your application and set you up with online ordering.
          </p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-3xl mx-auto space-y-4">
        <Section title="BUSINESS DETAILS">
          <F label="COMPANY NAME *"><input {...f('company_name')} className="seo-input" placeholder="Demon Tweeks Ltd" /></F>
          <F label="COMPANY REG. NUMBER"><input {...f('company_reg')} className="seo-input" placeholder="01049663" /></F>
          <F label="VAT NUMBER"><input {...f('vat_number')} className="seo-input" placeholder="GB123456789" /></F>
          <F label="D-U-N-S NUMBER (OPTIONAL)"><input {...f('duns_number')} className="seo-input" placeholder="232788118" /></F>
          <F label="PAYMENT TERMS (DAYS)"><input {...num('payment_terms_days')} className="seo-input" type="number" /></F>
          <F label="EORI NUMBER"><input {...f('eori_number')} className="seo-input" /></F>
          <F label="UKIMS NUMBER"><input {...f('ukims_number')} className="seo-input" /></F>
        </Section>

        <Section title="REGISTERED ADDRESS">
          <F label="STREET 1" full><input {...f('address_line1')} className="seo-input" placeholder="75 Ash Road South" /></F>
          <F label="STREET 2" full><input {...f('address_line2')} className="seo-input" placeholder="Wrexham Industrial Estate" /></F>
          <F label="TOWN"><input {...f('address_city')} className="seo-input" /></F>
          <F label="COUNTY"><input {...f('address_county')} className="seo-input" /></F>
          <F label="POST CODE"><input {...f('address_postcode')} className="seo-input" placeholder="LL13 9UG" /></F>
          <F label="COUNTRY"><input {...f('address_country')} className="seo-input" /></F>
        </Section>

        <Section title="CONTACT INFORMATION">
          <F label="CONTACT NAME"><input {...f('contact_name')} className="seo-input" placeholder="Jacob Mackriell" /></F>
          <F label="TRADE CONTACT"><input {...f('trade_contact')} className="seo-input" /></F>
          <F label="TELEPHONE"><input {...f('contact_phone')} className="seo-input" placeholder="01978 663664" /></F>
          <F label="TELEPHONE 2"><input {...f('phone_2')} className="seo-input" /></F>
          <F label="EMAIL 1 *"><input {...f('contact_email')} className="seo-input" type="email" placeholder="orders@yourcompany.co.uk" /></F>
          <F label="FAX"><input {...f('fax')} className="seo-input" /></F>
          <F label="SECONDARY EMAILS" full>
            <textarea {...f('secondary_emails')} rows={2} className="seo-input resize-y" placeholder="accounts@yourcompany.co.uk, buyer@yourcompany.co.uk (comma-separated)" />
          </F>
        </Section>

        <Section title="ONLINE & SOCIAL">
          <F label="WEBSITE"><input {...f('website')} className="seo-input" placeholder="https://yourcompany.co.uk" /></F>
          <F label="TWITTER"><input {...f('twitter')} className="seo-input" placeholder="twitter.com/yourcompany" /></F>
          <F label="LINKEDIN"><input {...f('linkedin')} className="seo-input" placeholder="linkedin.com/company/yourcompany" /></F>
          <F label="FACEBOOK"><input {...f('facebook')} className="seo-input" placeholder="facebook.com/yourcompany" /></F>
        </Section>

        <div className="border border-white/10 rounded p-5 bg-card">
          <p className="text-primary text-[10px] font-bold tracking-widest mb-3 pb-2 border-b border-primary/20">ANYTHING ELSE?</p>
          <textarea {...f('notes')} rows={3} className="seo-input resize-y" placeholder="Tell us about your business, what you stock, and what you're looking to order." />
        </div>

        <button
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-sm tracking-widest px-6 py-4 transition-colors"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? 'SUBMITTING…' : 'SUBMIT APPLICATION'}
        </button>
      </section>
    </div>
  );
}